import { ProgressIndicator, RadioButton, Text, TextLink } from '@oneaudi/unified-web-components';
import { htmlToText } from 'html-to-text';
import { MouseEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig, VATConfig } from '~/app/market-config';
import { useDebugMode } from '~/app/utils/debug-mode';
import { Disclaimer } from '~/components/disclaimer/disclaimer';
import { Prices } from '~/components/prices';
import { CollapsibleBox } from '~/components/selection-card-new/selection-card-description';
import { SelectionCardStyles } from '~/components/selection-card-new/styles/selection-card-styles';
import { PriceOrError } from '~/graphql/mapper';
import { CurrencyFormatterDisplayContext } from '~/hooks/use-currency-formatter';
import { DATA_TEST_ID } from '~/test/data-test-id';

export const CardInformation = ({
	children,
	alwaysVisible,
	linksSection,
	onDescriptionToggle,
	disclaimerHeadline,
	disclaimerContent,
}: {
	children?: ReactNode | ReactNode[];
	linksSection?: ReactNode | ReactNode[];
	alwaysVisible: boolean;
	onDescriptionToggle?: (value: boolean) => void;
	disclaimerHeadline?: string;
	disclaimerContent?: string;
}) => {
	const { i18n } = useI18n();
	const [showDescription, setShowDescription] = useState(alwaysVisible);

	useEffect(() => {
		setShowDescription(alwaysVisible);
	}, [alwaysVisible]);

	const toggleDescription = useCallback(
		(event: MouseEvent) => {
			event.stopPropagation();
			event.preventDefault();

			setShowDescription(!showDescription);
			if (onDescriptionToggle) {
				onDescriptionToggle(showDescription);
			}
		},
		[onDescriptionToggle, showDescription],
	);

	const reactNodesAvailable = (nodes: ReactNode | ReactNode[]) =>
		(Array.isArray(nodes) && nodes.filter(Boolean).length > 0) || (!Array.isArray(nodes) && !!nodes);

	const renderBox = reactNodesAvailable(children);

	const renderLinks = reactNodesAvailable(linksSection);

	return renderBox ? (
		<>
			<CollapsibleBox boxExpanded={!alwaysVisible}>
				<TextLink
					onClick={toggleDescription}
					variant="standalone"
					size="sm"
					icon={showDescription ? 'arrow-up' : 'arrow-down'}
					iconPosition="trailing">
					{showDescription ? i18n('ngc.actions.hideDetails') : i18n('ngc.actions.showDetails')}
				</TextLink>
			</CollapsibleBox>
			<CollapsibleBox boxExpanded={!alwaysVisible && showDescription}>
				<div className="spacer" />
			</CollapsibleBox>
			<CollapsibleBox boxExpanded={showDescription} className="selection-card-description">
				{children}
				{renderLinks ? (
					<div onClick={(event) => event.stopPropagation()} className="selection-card-description">
						{linksSection}
					</div>
				) : null}
				{disclaimerContent && (
					<div className="disclaimer-wrapper">
						<Disclaimer type="item" headline={disclaimerHeadline} content={disclaimerContent} />
					</div>
				)}
				{/* TODO: check
				 {disclaimer?.disclaimerText &&
					isDisclaimerAvailable(disclaimer.disclaimerStartDate, disclaimer.disclaimerEndDate) && (
						<div className="disclaimer-wrapper">
							<Disclaimer
								type="item"
								headline={disclaimer.disclaimerHeadline}
								content={disclaimer.disclaimerText}
							/>
						</div>
					)} */}
			</CollapsibleBox>
		</>
	) : renderLinks ? (
		<>
			<div onClick={(event) => event.stopPropagation()} className="selection-card-description">
				{linksSection}
				{disclaimerContent && (
					<div className="disclaimer-wrapper">
						<Disclaimer type="item" headline={disclaimerHeadline} content={disclaimerContent} />
					</div>
				)}
				{/* TODO: check
				 {disclaimer?.disclaimerText &&
					isDisclaimerAvailable(disclaimer.disclaimerStartDate, disclaimer.disclaimerEndDate) && (
						<div className="disclaimer-wrapper">
							<Disclaimer
								type="item"
								headline={disclaimer.disclaimerHeadline}
								content={disclaimer.disclaimerText}
							/>
						</div>
					)} */}
			</div>
		</>
	) : null;
};

type CardItemInfo = {
	name: string;
	selected: boolean;
	loading: boolean;
	readonly: boolean;
	standard: boolean;
	onClick: () => void;
	price?: {
		totalPrice?: PriceOrError;
		monthlyRate?: PriceOrError;
		trimlineAndEngine: VATConfig;
		displayContext?: CurrencyFormatterDisplayContext | undefined;
	};
	incentives?: ReactNode;
	prCode?: string;
	year?: string;
	debugCode?: string;
	onDescriptionToggle: (value: boolean) => void;
};

export const TrimlineSelectionCard = ({
	item: {
		name,
		selected,
		readonly,
		debugCode,
		loading,
		price,
		standard,
		onClick,
		incentives,
		prCode,
		year,
		onDescriptionToggle,
	},
	textLinks,
	children,
}: {
	item: CardItemInfo;
	children?: ReactNode;
	textLinks?: ReactNode;
}) => {
	const { i18n } = useI18n();
	const { isDebug } = useDebugMode();
	const { config } = useMarketConfig();
	const code = `[${debugCode ?? `${prCode}-${year}`}]`;
	const displayName = isDebug ? `${name} ${code}` : name;

	return (
		<SelectionCardStyles
			isDebug={isDebug}
			hasTechData={config.hasTechnicalDataEnabled}
			data-testid={DATA_TEST_ID.SELECTION_CARD_CONTAINER}
			className={`${readonly ? 'is-readonly' : ''} ${selected ? 'is-selected' : ''}`}
			onClick={onClick}>
			<div className="selection-card-content">
				<div className="selection-card-header">
					<div className="selection-card-title">
						<RadioButton
							label={htmlToText(displayName)}
							name=""
							onChange={() => ({})}
							checked={selected}
							id=""
							value=""
							className="top-item"
							data-testid={DATA_TEST_ID.SELECTION_CARD_CHECKBOX}
						/>
						{loading ? (
							<ProgressIndicator className="loading-item" variant={'spinner'} type={'infinite'} />
						) : null}
					</div>
					{incentives}
					{price &&
						(standard ? (
							<Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
								{i18n('ngc.common.standard')}
							</Text>
						) : (
							<Prices
								totalPrice={price.totalPrice}
								monthlyRate={price.monthlyRate}
								vatConfig={price.trimlineAndEngine}
								displayContext={price.displayContext}
							/>
						))}
				</div>
				<CardInformation
					onDescriptionToggle={onDescriptionToggle}
					alwaysVisible={selected}
					linksSection={textLinks}>
					{children}
				</CardInformation>
			</div>
		</SelectionCardStyles>
	);
};
